import React, { useState } from 'react';
import { Popover, IconButton, Tooltip, Typography } from '@material-ui/core';
import DateRangeIcon from '@material-ui/icons/DateRange';
import ClearIcon from '@material-ui/icons/Clear';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import moment from 'moment';

const PRIMARY       = '#3f51b5';
const PRIMARY_LIGHT = '#e8eaf6';
const PRIMARY_DARK  = '#283593';
const IN_RANGE_BG   = '#c5cae9';

const DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];

const isSame  = (a, b) => a && b && a.isSame(b, 'day');
const isInRange = (day, from, to) => from && to && day.isAfter(from, 'day') && day.isBefore(to, 'day');
const isStart = (day, from) => isSame(day, from);
const isEnd   = (day, to)   => isSame(day, to);
const isToday = (day)       => day.isSame(moment(), 'day');

const CalendarMonth = ({ month, from, to, hovered, onDayClick, onDayHover }) => {
    const start = month.clone().startOf('month');
    const daysInMonth = month.daysInMonth();
    // Monday-based week offset
    const offset = (start.isoWeekday() - 1 + 7) % 7;

    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(start.clone().date(d));

    const effectiveTo = to || hovered;

    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));

    return (
        <div>
            {/* Month title */}
            <Typography
                variant="subtitle2"
                style={{ textAlign: 'center', color: PRIMARY, fontWeight: 600, marginBottom: 6, textTransform: 'capitalize' }}
            >
                {month.format('MMMM YYYY')}
            </Typography>

            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 32px)', gap: 1 }}>
                {DAYS.map(d => (
                    <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', color: '#888', fontWeight: 600, padding: '2px 0' }}>
                        {d}
                    </div>
                ))}

                {/* Day cells */}
                {cells.map((day, idx) => {
                    if (!day) return <div key={`e-${idx}`} />;

                    const _isStart   = isStart(day, from);
                    const _isEnd     = isEnd(day, effectiveTo);
                    const _isInRange = isInRange(day, from, effectiveTo);
                    const _isToday   = isToday(day);
                    const _isSingle  = _isStart && isSame(from, effectiveTo);

                    let bg = 'transparent';
                    let color = '#333';
                    let borderRadius = '50%';
                    let fontWeight = 'normal';

                    if (_isStart || _isEnd) {
                        bg = PRIMARY;
                        color = 'white';
                        fontWeight = 600;
                    } else if (_isInRange) {
                        bg = IN_RANGE_BG;
                        color = PRIMARY_DARK;
                        borderRadius = '0';
                    }

                    if (_isStart && !_isSingle && effectiveTo) borderRadius = '50% 0 0 50%';
                    if (_isEnd   && !_isSingle && from)        borderRadius = '0 50% 50% 0';

                    return (
                        <div
                            key={day.valueOf()}
                            onClick={() => onDayClick(day)}
                            onMouseEnter={() => onDayHover(day)}
                            style={{
                                width: 32, height: 32,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                                background: bg,
                                borderRadius,
                                color,
                                fontWeight,
                                fontSize: '0.8rem',
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                                border: _isToday && !_isStart && !_isEnd ? `1px solid ${PRIMARY}` : 'none',
                                boxSizing: 'border-box',
                                transition: 'background 0.1s',
                            }}
                        >
                            {day.date()}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const DateRangeFilter = ({ columnDef, onFilterChanged }) => {
    const [anchorEl, setAnchorEl]   = useState(null);
    const [from, setFrom]           = useState(null);   // moment | null
    const [to, setTo]               = useState(null);   // moment | null
    const [hovered, setHovered]     = useState(null);
    const [selecting, setSelecting] = useState(false);  // false = picking start, true = picking end
    const [month, setMonth]         = useState(moment().startOf('month'));

    const notify = (newFrom, newTo) => {
        onFilterChanged(columnDef.tableData.id, {
            from: newFrom ? newFrom.format('YYYY-MM-DD') : '',
            to:   newTo   ? newTo.format('YYYY-MM-DD')   : '',
        });
    };

    const handleDayClick = (day) => {
        if (!selecting) {
            setFrom(day);
            setTo(null);
            setSelecting(true);
            notify(day, null);
        } else {
            if (day.isBefore(from, 'day')) {
                setFrom(day);
                setTo(from);
                notify(day, from);
            } else {
                setTo(day);
                notify(from, day);
            }
            setSelecting(false);
            setHovered(null);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setFrom(null);
        setTo(null);
        setSelecting(false);
        setHovered(null);
        onFilterChanged(columnDef.tableData.id, { from: '', to: '' });
    };

    const hasFilter = from || to;

    const fmt = (m) => m ? m.format('DD/MM/YYYY') : '...';
    const rangeText = hasFilter ? `${fmt(from)} – ${fmt(to)}` : null;

    return (
        <>
            <Tooltip title={rangeText || 'Filtrar por fecha'} placement="top">
                <div
                    onClick={e => setAnchorEl(e.currentTarget)}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        cursor: 'pointer', borderRadius: 4, padding: '3px 6px',
                        background: hasFilter ? PRIMARY_LIGHT : 'transparent',
                        border: `1px solid ${hasFilter ? PRIMARY : '#bdbdbd'}`,
                        color: hasFilter ? PRIMARY : '#9e9e9e',
                        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                        fontSize: '0.75rem', maxWidth: 130, minWidth: 24,
                        transition: 'all 0.2s', userSelect: 'none',
                    }}
                >
                    <DateRangeIcon style={{ fontSize: 15, flexShrink: 0 }} />
                    {rangeText && (
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {rangeText}
                        </span>
                    )}
                    {hasFilter && (
                        <ClearIcon style={{ fontSize: 13, flexShrink: 0, color: PRIMARY }} onClick={handleClear} />
                    )}
                </div>
            </Tooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => { setAnchorEl(null); setSelecting(false); setHovered(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ style: { borderRadius: 6, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', overflow: 'visible' } }}
            >
                {/* Header */}
                <div style={{
                    background: PRIMARY, padding: '10px 16px',
                    display: 'flex', alignItems: 'center', gap: 8,
                    borderRadius: '6px 6px 0 0',
                }}>
                    <DateRangeIcon style={{ color: 'white', fontSize: 18 }} />
                    <span style={{ color: 'white', fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontWeight: 500, fontSize: '0.9rem' }}>
                        {selecting ? 'Selecciona fecha fin' : 'Selecciona fecha inicio'}
                    </span>
                    {hasFilter && (
                        <span style={{
                            marginLeft: 'auto', color: 'white', opacity: 0.85,
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif', fontSize: '0.78rem',
                        }}>
                            {fmt(from)} – {fmt(to)}
                        </span>
                    )}
                </div>

                {/* Calendar body */}
                <div style={{ padding: '12px 16px 8px' }}>
                    {/* Month navigation */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <IconButton size="small" onClick={() => setMonth(month.clone().subtract(1, 'month'))}>
                            <ChevronLeftIcon style={{ color: PRIMARY }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setMonth(month.clone().add(1, 'month'))}>
                            <ChevronRightIcon style={{ color: PRIMARY }} />
                        </IconButton>
                    </div>

                    <CalendarMonth
                        month={month}
                        from={from}
                        to={to}
                        hovered={selecting ? hovered : null}
                        onDayClick={handleDayClick}
                        onDayHover={setHovered}
                    />
                </div>

                {/* Footer */}
                {hasFilter && (
                    <div style={{ padding: '6px 16px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                        <span
                            onClick={() => { setFrom(null); setTo(null); setSelecting(false); setHovered(null); onFilterChanged(columnDef.tableData.id, { from: '', to: '' }); }}
                            style={{
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                                fontSize: '0.8rem', fontWeight: 500, color: PRIMARY,
                                cursor: 'pointer', textDecoration: 'underline', userSelect: 'none',
                            }}
                        >
                            Limpiar filtro
                        </span>
                    </div>
                )}
            </Popover>
        </>
    );
};

export default DateRangeFilter;
